import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, User, LogOut, LogIn, Menu, X } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

const TrippyNavbar = ({ isCartOpen, setIsCartOpen, isMobileMenuOpen, setIsMobileMenuOpen, handleCartToggle, handleMobileMenuToggle, navLinks = [] }) => {
  const location = useLocation();
  const { cartItems } = useCart();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const userMenuRef = useRef(null);

  const getCartItemCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  // Handle responsive breakpoint - Simple mobile detection
  useEffect(() => {
    const checkMobile = () => {
      // Simple check: if it's a phone/tablet (touch device) OR width is small
      // Use 1024px breakpoint to catch tablets in landscape
      const width = window.innerWidth;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isPhone = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      // Always mobile if it's a phone/tablet, OR if width <= 1024px
      setIsMobile(isPhone || (isTouchDevice && width <= 1024) || width <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleNavLinkClick = () => {
    if (setIsMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
    <nav style={{
      background: 'rgba(0, 0, 0, 0.9)',
      backdropFilter: 'blur(10px)',
      borderBottom: '2px solid rgba(6, 182, 212, 0.3)',
      padding: '16px 0',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      width: '100%',
      overflowX: 'hidden',
      overflowY: 'visible'
    }}>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '0 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Logo */}
        <Link 
          to="/" 
          onClick={handleNavLinkClick}
          style={{
            fontSize: 'clamp(18px, 4vw, 24px)',
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0
          }}
        >
          <img 
            src="/favicon.png" 
            alt="Rainbow Sails" 
            style={{
              width: 'clamp(24px, 5vw, 28px)',
              height: 'clamp(24px, 5vw, 28px)'
            }}
          />
          <span style={{ whiteSpace: 'nowrap' }}>Rainbow Sails</span>
        </Link>

        {/* Desktop Navigation Links */}
        <div style={{
          display: isMobile ? 'none' : 'flex',
          gap: '32px',
          alignItems: 'center'
        }}>
          {navLinks.map(link => (
            <Link 
              key={link.to}
              to={link.to}
              style={{
                color: location.pathname === link.to ? '#06b6d4' : 'white',
                textDecoration: 'none',
                fontWeight: location.pathname === link.to ? 'bold' : 'normal',
                whiteSpace: 'nowrap',
                fontSize: '16px'
              }}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Desktop Authentication Section */}
        <div style={{
          display: isMobile ? 'none' : 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          {user ? (
            /* User is logged in - Show Profile Menu */
            <div ref={userMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  background: 'rgba(139, 92, 246, 0.2)',
                  border: '2px solid #8b5cf6',
                  color: '#8b5cf6',
                  padding: '12px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(139, 92, 246, 0.3)';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(139, 92, 246, 0.2)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <User size={20} />
                <span style={{ fontWeight: 'bold' }}>
                  {user.firstName || 'Profile'}
                </span>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  marginTop: '8px',
                  background: 'rgba(0, 0, 0, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '12px',
                  padding: '8px 0',
                  minWidth: '200px',
                  zIndex: 1000,
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)'
                }}>
                  <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    {user.email}
                  </div>
                  <Link
                    to="/profile"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      color: 'white',
                      textDecoration: 'none',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(139, 92, 246, 0.2)'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    onClick={() => setShowUserMenu(false)}
                  >
                    <User size={16} />
                    My Profile
                  </Link>
                  <Link
                    to="/profile"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      color: 'white',
                      textDecoration: 'none',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(139, 92, 246, 0.2)'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    onClick={() => setShowUserMenu(false)}
                  >
                    <ShoppingBag size={16} />
                    My Orders
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setShowUserMenu(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 16px',
                      color: '#ef4444',
                      background: 'transparent',
                      border: 'none',
                      width: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.2)'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* User is not logged in - Show Login Button */
            <Link
              to="/auth"
              style={{
                background: 'rgba(236, 72, 153, 0.2)',
                border: '2px solid #ec4899',
                color: '#ec4899',
                padding: '12px 20px',
                borderRadius: '12px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: 'bold',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(236, 72, 153, 0.3)';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(236, 72, 153, 0.2)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              <LogIn size={20} />
              Login
            </Link>
          )}
        </div>

        {/* Desktop Cart Button */}
        <button
          onClick={(e) => {
            const timestamp = new Date().toISOString();
            const buttonId = 'DESKTOP_CART_BUTTON';
            
            console.log('\n' + '='.repeat(80));
            console.log(`🔘 [BUTTON PRESS] ${buttonId} - Desktop Cart Button Clicked`);
            console.log(`⏰ [TIMESTAMP] ${timestamp}`);
            console.log(`📍 [LOCATION] TrippyNavbar.js:Desktop Cart Button`);
            console.log('='.repeat(80));
            
            console.log(`📊 [EVENT DATA] Event:`, e);
            console.log(`📊 [STATE] Current cart state:`, isCartOpen);
            console.log(`📊 [STATE] Current menu state:`, isMobileMenuOpen);
            
            console.log(`🛑 [ACTION] Stopping event propagation`);
            e.stopPropagation();
            console.log(`✅ [RESULT] Event propagation stopped`);
            
            console.log(`🔄 [FUNCTION CALL] Calling handleCartToggle()`);
            console.log(`📤 [DATA FLOW] handleCartToggle() → App.js → handleCartToggle()`);
            handleCartToggle();
            console.log(`✅ [RESULT] handleCartToggle() completed`);
            console.log('='.repeat(80) + '\n');
          }}
          style={{
            position: 'relative',
            background: 'rgba(6, 182, 212, 0.2)',
            border: '2px solid #06b6d4',
            color: '#06b6d4',
            padding: '12px',
            borderRadius: '12px',
            cursor: 'pointer',
            display: isMobile ? 'none' : 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(6, 182, 212, 0.3)';
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(6, 182, 212, 0.2)';
            e.target.style.transform = 'scale(1)';
          }}
        >
          <ShoppingBag size={20} />
          <span style={{ fontWeight: 'bold' }}>
            Cart ({getCartItemCount()})
          </span>
          {getCartItemCount() > 0 && (
            <div style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              background: '#ec4899',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {getCartItemCount()}
            </div>
          )}
        </button>

        {/* Mobile Controls - Always show on mobile: Menu, Login, Cart */}
        {isMobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0
          }}>
            {/* Mobile Menu Button */}
            <button
              data-mobile-menu-toggle
              onClick={(e) => {
                const timestamp = new Date().toISOString();
                const buttonId = 'MOBILE_MENU_BUTTON';
                
                console.log('\n' + '='.repeat(80));
                console.log(`🔘 [BUTTON PRESS] ${buttonId} - Mobile Menu Button Clicked`);
                console.log(`⏰ [TIMESTAMP] ${timestamp}`);
                console.log(`📍 [LOCATION] TrippyNavbar.js:Mobile Menu Button`);
                console.log('='.repeat(80));
                
                console.log(`📊 [EVENT DATA] Event:`, e);
                console.log(`📊 [EVENT DATA] Event target:`, e.target);
                console.log(`📊 [STATE] Current menu state:`, isMobileMenuOpen);
                console.log(`📊 [STATE] Current cart state:`, isCartOpen);
                
                console.log(`🛑 [ACTION] Stopping event propagation`);
                e.stopPropagation();
                console.log(`✅ [RESULT] Event propagation stopped`);
                
                console.log(`🔄 [FUNCTION CALL] Calling handleMobileMenuToggle()`);
                console.log(`📤 [DATA FLOW] handleMobileMenuToggle() → App.js → handleMobileMenuToggle()`);
                handleMobileMenuToggle();
                console.log(`✅ [RESULT] handleMobileMenuToggle() completed`);
                console.log('='.repeat(80) + '\n');
              }}
              style={{
                background: 'rgba(6, 182, 212, 0.2)',
                border: '2px solid #06b6d4',
                color: '#06b6d4',
                padding: '10px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Mobile Login Button */}
            {!user && (
              <Link
                to="/auth"
                onClick={handleNavLinkClick}
                style={{
                  background: 'rgba(236, 72, 153, 0.2)',
                  border: '2px solid #ec4899',
                  color: '#ec4899',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  flexShrink: 0,
                  whiteSpace: 'nowrap'
                }}
              >
                <LogIn size={18} />
                Login
              </Link>
            )}

            {/* Mobile User Menu (when logged in) */}
            {user && (
              <div ref={userMenuRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  style={{
                    background: 'rgba(139, 92, 246, 0.2)',
                    border: '2px solid #8b5cf6',
                    color: '#8b5cf6',
                    padding: '10px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <User size={20} />
                </button>
                {showUserMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    background: 'rgba(0, 0, 0, 0.95)',
                    border: '2px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '8px',
                    padding: '8px',
                    minWidth: '150px',
                    zIndex: 1001,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                  }}>
                    <Link
                      to="/profile"
                      onClick={() => setShowUserMenu(false)}
                      style={{
                        display: 'block',
                        padding: '8px 12px',
                        color: 'white',
                        textDecoration: 'none',
                        fontSize: '14px',
                        borderRadius: '4px',
                        marginBottom: '4px'
                      }}
                    >
                      Profile
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '14px',
                        textAlign: 'left',
                        borderRadius: '4px'
                      }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Mobile Cart Button */}
            <button
              onClick={(e) => {
                const timestamp = new Date().toISOString();
                const buttonId = 'MOBILE_CART_BUTTON';
                
                console.log('\n' + '='.repeat(80));
                console.log(`🔘 [BUTTON PRESS] ${buttonId} - Mobile Cart Button Clicked`);
                console.log(`⏰ [TIMESTAMP] ${timestamp}`);
                console.log(`📍 [LOCATION] TrippyNavbar.js:Mobile Cart Button`);
                console.log('='.repeat(80));
                
                console.log(`📊 [EVENT DATA] Event:`, e);
                console.log(`📊 [EVENT DATA] Event target:`, e.target);
                console.log(`📊 [STATE] Current cart state:`, isCartOpen);
                console.log(`📊 [STATE] Current menu state:`, isMobileMenuOpen);
                
                console.log(`🛑 [ACTION] Stopping event propagation`);
                e.stopPropagation();
                console.log(`✅ [RESULT] Event propagation stopped`);
                
                console.log(`🔄 [FUNCTION CALL] Calling handleCartToggle()`);
                console.log(`📤 [DATA FLOW] handleCartToggle() → App.js → handleCartToggle()`);
                handleCartToggle();
                console.log(`✅ [RESULT] handleCartToggle() completed`);
                console.log('='.repeat(80) + '\n');
              }}
              style={{
                position: 'relative',
                background: 'rgba(6, 182, 212, 0.2)',
                border: '2px solid #06b6d4',
                color: '#06b6d4',
                padding: '10px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <ShoppingBag size={20} />
              {getCartItemCount() > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  background: '#ec4899',
                  color: 'white',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 'bold'
                }}>
                  {getCartItemCount()}
                </div>
              )}
            </button>
          </div>
        )}
      </div>
    </nav>
  </>
  );
};

export default TrippyNavbar;
